import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../../../assets/logo_round.png";
import { BRAND_NAME, APP_VERSION } from "../../../common/constants";
import { useAuth } from "../../context/AuthContext";
import {
  FaUser,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaCrown,
  FaRocket,
  FaClipboardList,
  FaBookOpen,
  FaLifeRing,
  FaGift,
} from "react-icons/fa";
import "./Navbar.css";


const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSupportAlert, setShowSupportAlert] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
    setProfileDropdownOpen(false);
  };

  const displayName =
    user?.name?.trim() || (user?.email ? user.email.split("@")[0] : "User");

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const hasProfileImage = Boolean(user?.picture);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <nav className="nav">
        {/* BRAND */}
        <div className="logo-wrap" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <img src={logo} alt="Logo" className="logo" />
          <div className="brand-wrap">
            <h1 className="brand">{BRAND_NAME}</h1>
            <span className="brand-accent" />
          </div>
        </div>

        {/* MOBILE MENU ICON */}
        <div
          className="mobile-menu-icon"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </div>

        {/* RIGHT MENU */}
        <div className={`right-wrap ${mobileOpen ? "mobile-menu active" : ""}`}>
          <NavLink to="/" current={location.pathname} onNavigate={() => setMobileOpen(false)}>Home</NavLink>
          <NavLink to="/how-it-works" current={location.pathname} onNavigate={() => setMobileOpen(false)}>How It Works</NavLink>
          <NavLink to="/events" current={location.pathname} onNavigate={() => setMobileOpen(false)}>Events</NavLink>
          <NavLink to="/courses" current={location.pathname} onNavigate={() => setMobileOpen(false)}>Courses</NavLink>
          <NavLink to="/study" current={location.pathname} onNavigate={() => setMobileOpen(false)}>Study</NavLink>
          <NavLink to="/plans" current={location.pathname} onNavigate={() => setMobileOpen(false)}>Plans</NavLink>
          <NavLink to="/reviews" current={location.pathname} onNavigate={() => setMobileOpen(false)}>Reviews</NavLink>

          {/* ===== DESKTOP PROFILE DROPDOWN ===== */}
          {user && (
            <div className="desktop-only" ref={dropdownRef}>
              <div
                className="profile-icon"
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              >
                {hasProfileImage ? (
                  <img src={user.picture} alt={displayName} className="avatar" />
                ) : (
                  <div className="default-avatar">{getInitials(displayName)}</div>
                )}
              </div>

              {profileDropdownOpen && (
                <div className="profile-dropdown">
                  <DropdownItem icon={<FaUser />} onClick={() => { navigate("/profile"); setProfileDropdownOpen(false); }}>
                    Profile
                  </DropdownItem>
                  <DropdownItem icon={<FaBookOpen />} onClick={() => { navigate("/my-courses"); setProfileDropdownOpen(false); }}>
                    My Courses
                  </DropdownItem>
                  <DropdownItem icon={<FaClipboardList />} onClick={() => { navigate("/my-active-plan"); setProfileDropdownOpen(false); }}>
                    My Plan
                  </DropdownItem>
                  <DropdownItem icon={<FaCrown />} onClick={() => { navigate("/plans"); setProfileDropdownOpen(false); }}>
                    Go Premium
                  </DropdownItem>
                  <DropdownItem icon={<FaLifeRing />} onClick={() => { navigate("/support"); setProfileDropdownOpen(false); }}>
                    Support
                  </DropdownItem>
                  <DropdownItem icon={<FaGift />} onClick={() => { navigate("/referral"); setProfileDropdownOpen(false); }}>
                    Referral
                  </DropdownItem>
                  <DropdownItem icon={<FaRocket />} onClick={() => { navigate("/how-it-works"); setProfileDropdownOpen(false); }}>
                    How It Works
                  </DropdownItem>
                  <DropdownItem icon={<FaSignOutAlt />} danger onClick={handleLogout}>
                    Logout
                  </DropdownItem>
                  <div className="version">App Version {APP_VERSION}</div>
                </div>
              )}
            </div>
          )}

          {/* ===== MOBILE ACCOUNT ===== */}
          {user && (
            <div className="mobile-account-section">
              <div className="mobile-divider" />
              <div className="mobile-profile">
                <div className="profile-left">
                  {hasProfileImage ? (
                    <img src={user.picture} alt={displayName} className="avatar" />
                  ) : (
                    <div className="default-avatar">{getInitials(displayName)}</div>
                  )}
                  <span style={{ fontWeight: 700 }}>{displayName}</span>
                </div>
              </div>
              <div className="mobile-menu-links">
                {[
                  { icon: <FaUser />,          label: "Profile",      path: "/profile" },
                  { icon: <FaBookOpen />,       label: "My Courses",   path: "/my-courses" },
                  { icon: <FaClipboardList />,  label: "My Plan",      path: "/my-active-plan" },
                  { icon: <FaCrown />,          label: "Go Premium",   path: "/plans" },
                  { icon: <FaLifeRing />,       label: "Support",      path: "/support" },
                  { icon: <FaGift />,           label: "Referral",     path: "/referral" },
                  { icon: <FaRocket />,         label: "How It Works", path: "/how-it-works" },
                ].map(({ icon, label, path }) => (
                  <div key={path} className="mobile-menu-link"
                    onClick={() => { navigate(path); setMobileOpen(false); }}
                  >
                    {icon}<span>{label}</span>
                  </div>
                ))}
              </div>
              <button className="mobile-logout-btn" onClick={() => { setMobileOpen(false); handleLogout(); }}>
                <FaSignOutAlt /> Logout
              </button>
              <div className="version">App Version {APP_VERSION}</div>
            </div>
          )}

          {/* ===== DESKTOP AUTH ===== */}
          {!user && (
            <div className="auth-actions">
              <Link to="/login" className="login-btn">
                Login
              </Link>
              <Link to="/register" className="register-btn">
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* SUPPORT ALERT MODAL */}
      {showSupportAlert && (
        <div className="overlay">
          <div className="modal">
            <h3>Login Required</h3>
            <p>You need to login or register to raise a support ticket.</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button className="dismiss-btn" onClick={() => setShowSupportAlert(false)}>
                Dismiss
              </button>
              <button className="register-btn" onClick={() => navigate("/register")}>
                Go to Registration
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ================= REUSABLE ================= */
const NavLink = ({ to, current, children, onNavigate }) => {
  const navigate = useNavigate();
  return (
    <span
      className={`nav-link${current === to ? " nav-link-active" : ""}`}
      onClick={() => {
        navigate(to);
        onNavigate?.();
      }}
      style={{ cursor: "pointer" }}
    >
      {children}
    </span>
  );
};

const DropdownItem = ({ icon, children, onClick, danger }) => (
  <div
    className={`dropdown-item ${danger ? "danger" : ""}`}
    onClick={onClick}
  >
    {icon}
    <span>{children}</span>
  </div>
);

export default Navbar;
